import { Menu, Transition } from '@headlessui/react';
import FadeTransition from '@ui/Transitions/FadeTransition';
import Typography from '@ui/Typography';
import { getPostLink } from '@utils/post.helper';
import { joinUrl } from '@utils/stringHelpers';
import useDonateDialogStore from '@/components/store/donateDialogSlice';
import useStripeDonateDialogStore from '../store/stripeDonateDialogSlice';
import useShareModalStore from '@/components/store/shareModalSlice';
import { cx } from 'cva';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import {
  HiDotsHorizontal,
  HiHeart,
  HiOutlineHeart,
  HiOutlineLockOpen,
  HiOutlineShare,
} from 'react-icons/hi';
import { BiShow, BiHide } from 'react-icons/bi';
import useUserProfile from '@/hooks/useUserProfile';
import {
  useIsCommunityAdmin,
  useIsCompanyAdmin,
  useIsNekiAdmin,
} from '@/hooks/useUser';
import { TargetViewers } from '@/constants/profileType.constant';
import { includes } from 'lodash-es';
import PostType, { CampaignType } from '@/constants/postType.constants';
import { AiOutlineEdit } from 'react-icons/ai';
import { RiDeleteBin5Line } from 'react-icons/ri';
import { MdOutlineFeaturedVideo } from 'react-icons/md';
import { IoFlagSharp } from 'react-icons/io5';
import useConfirmDialogStore from '../Dialogs/confirmDialog.store';
import {
  useDeletePost,
  useReportPost,
  useToggleNewsHomeFeedVisibility,
  useTogglePostFeatured,
  useTogglePostPublic,
} from '@/actions/feed.actions';
import CreatePostDialog from './CreatePostDialog/CreatePostDialog';
import useHasUserSession from '@/hooks/useHasUserSession';
import {
  fetchUserLocation,
  recordDonationClick,
  recordShareClick,
} from '@/actions/public/server.actions';

const editablePostTypes = [PostType.UserPost /*PostType.Celebration*/];

function PostMenu(props) {
  const {
    post,
    withDonate = false,
    recordDonationClickPayload = null,
    recordShareClickPayload = null,
  } = props;
  const ownerOrg = post.renderProps.company;

  const userProfile = useUserProfile();
  const isCommunityAdmin = useIsCommunityAdmin();
  const isNekiAdmin = useIsNekiAdmin();
  const hasSession = useHasUserSession();

  const hasPublicProfile =
    userProfile.activeCommunity.publicProfile?.enabled || false;

  const isVisibleToAll = post.targetViewers === TargetViewers.All;
  const isPostOwnedByOrg = ownerOrg.id === userProfile.activeCommunityId;

  const isP2PFundraiser =
    post.sourcePostData.fundRaiseType === CampaignType.P2PFundRaise;

  const isPostPublic =
    isVisibleToAll &&
    (isPostOwnedByOrg ||
      includes(post.publicShares?.publishedBy, userProfile.activeCommunityId));


  // const canMakePostPublic =
  //   hasPublicProfile &&
  //   (isCommunityAdmin || isNekiAdmin) &&
  //   (isVisibleToAll || isPostOwnedByOrg);

  // const canMakePostPublic =
  //   hasPublicProfile && (isCommunityAdmin || isNekiAdmin) && isPostOwnedByOrg;

  const canMakePostPublic =
    hasPublicProfile && (isCommunityAdmin || isNekiAdmin);

  const canMakePostFeatured =
    (hasPublicProfile || isCommunityAdmin) &&
    // ((isPostPublic && isCommunityAdmin) || isNekiAdmin) &&
    (isCommunityAdmin || isNekiAdmin) &&
    post.sourcePostType !== PostType.NewsFeeds;

  const canEdit =
    userProfile.id === post.createdBy &&
    userProfile.activeCommunityId === post.companyId &&
    editablePostTypes.includes(post.sourcePostType);

  const canDelete = userProfile.id === post.createdBy || isNekiAdmin;

  const isPostFeatured = isPostOwnedByOrg
    ? post.isFeatured
    : includes(post.publicShares?.featuredBy, userProfile.activeCommunityId);

  const canToggleNewsHomeFeedVisibility =
    isNekiAdmin && post.sourcePostType === PostType.NewsFeeds;
  const canMakeNewsVisibleOnHomeFeed =
    !post.homeFeedVisibility && canToggleNewsHomeFeedVisibility;

  const { seekConfirmation } = useConfirmDialogStore();
  const { mutate: deletePost } = useDeletePost();
  const { mutate: reportPost } = useReportPost();
  const { mutateAsync: togglePostPublic } = useTogglePostPublic();
  const { mutateAsync: togglePostFeatured } = useTogglePostFeatured();
  const { mutateAsync: toggleNewsHomeFeedVisibility } =
    useToggleNewsHomeFeedVisibility();

  const [editPostDialogOpen, setEditPostDialogOpen] = useState(false);

  /****** Share modal******/
  const setShowShareModal = useShareModalStore((state) => state.setOpen);
  const handleShare = () => {
    setShowShareModal(true, {
      url: joinUrl(window.location.origin, getPostLink(post)),
      title: post.renderProps.title,
      description: post.renderProps.description,
    });

    if (recordShareClickPayload) {
      // setTimeout(() => {
      recordShareClick(recordShareClickPayload);
      // }, 2000);
    }
  };

  /****** Donate modal******/
  const setShowDonateDialog = useDonateDialogStore((state) => state.setOpen);
  const setShowStripeDonateDialog = useStripeDonateDialogStore(
    (state) => state.setOpen,
  );
  const canReceiveDonations = post.renderProps.nonProfit?.canReceiveDonations;
  const handleDonate = async () => {
    if (canReceiveDonations) {
      // setShowDonateDialog(true, {
      //   nonProfit: post.renderProps.nonProfit,
      //   amount: '',
      //   post: post,
      // });
      if (post.renderProps.nonProfit.stripe_id) {
        setShowStripeDonateDialog(true, {
          nonProfit: post.renderProps.nonProfit,
          amount: '',
          post: post,
        });
      } else {
        setShowDonateDialog(true, {
          nonProfit: post.renderProps.nonProfit,
          amount: '',
          post: post,
        });
      }

      if (recordDonationClickPayload) {
        if (!hasSession) {
          const userLocation = await fetchUserLocation();
          if (userLocation) {
            recordDonationClickPayload.donationClick.location = userLocation;
          }
        }

        // setTimeout(() => {
        recordDonationClick(recordDonationClickPayload);
        // }, 5000);
      }
    }
  };

  const handleEdit = () => {
    setEditPostDialogOpen(true);
  };

  const handleToggleNewsHomeFeedVisibility = async () => {
    const confirmed = await seekConfirmation(
      canMakeNewsVisibleOnHomeFeed
        ? 'Show on home feed'
        : 'Hide from home feed',
      canMakeNewsVisibleOnHomeFeed
        ? 'This post will be visible on the home feed. Are you sure to make it visible on the home feed?'
        : 'This post will disappear from the home feed. Are you sure to make it disappear from the home feed?',
      'Yes, Go ahead',
    );

    if (confirmed) {
      await toggleNewsHomeFeedVisibility({
        postId: post.id,
        isVisibleOnHomeFeed: canMakeNewsVisibleOnHomeFeed,
      });

      seekConfirmation(
        canMakeNewsVisibleOnHomeFeed
          ? 'Post visible on home feed'
          : 'Post hidden from home feed',
        canMakeNewsVisibleOnHomeFeed
          ? 'This post is now visible on the home feed.'
          : 'This post is now hidden from the home feed.',
        'Close',
      );
    }
  };

  const handleMakePostPublic = async () => {
    const isPublic = isPostPublic;
    const confirmed = await seekConfirmation(
      isPublic ? 'Make post private' : 'Make post public',
      isPublic
        ? 'This post will be visible to only your organization. Are you sure you want to make this post private?'
        : 'This post will be visible to all users. Are you sure you want to make this post public?',
      'Yes, Go ahead',
    );

    if (confirmed) {
      await togglePostPublic({
        postId: post.id,
        isPublic: !isPublic,
      });

      seekConfirmation(
        isPublic ? 'Post made private' : 'Post made public',
        isPublic ? (
          'This post is now visible only to your organization.'
        ) : (
          <Typography variant="body2" component="div">
            This post is now publicly visible{' '}
            <a
              href={getPostLink(post)}
              className="text-text-hyperlink"
              target="_blank">
              here
            </a>
          </Typography>
        ),
        'Close',
      );
    }
  };

  const handleTogglePostAsFeatured = async () => {
    const featured = isPostFeatured;
    let featuredText = featured
      ? 'This post will no longer be visible in the featured posts section. Are you sure you want to remove this post from featured posts?'
      : 'This post will be visible in the featured posts section. Are you sure you want to make this post featured?';

    if (!isPostPublic) {
      featuredText = featured
        ? 'This post will no longer be visible in the home page featured section. Are you sure you want to remove this post from home page featured section?'
        : 'This post will be visible in the home page featured section. Are you sure you want to make this post featured in the home page?';
    }
    const confirmed = await seekConfirmation(
      featured ? 'Remove from featured posts' : 'Make post featured',
      featuredText,
      'Yes, Go ahead',
    );

    if (confirmed) {
      await togglePostFeatured({
        postId: post.id,
        isFeatured: !featured,
      });

      if (!featured) {
        await seekConfirmation(
          'Post is featured',
          <Typography variant="body2" component="div">
            The post is listed as featured in your{' '}
            {isPostPublic ? (
              <a
                href={`/${post.renderProps.company.slug}`}
                className="text-text-hyperlink"
                target="_blank">
                organization&apos;s public page
              </a>
            ) : (
              'home page at the top'
            )}
          </Typography>,
          'Close',
        );
      }
    }
  };

  const handleReportPost = async () => {
    const confirmed = await seekConfirmation(
      'Report post',
      'If you report this post, an email will be sent to the admin and we may reach out to you to better understand this issue. Are you sure you want to report this post?',
      'Yes, Go ahead',
    );

    if (confirmed) {
      reportPost({ postId: post.id });
    }
  };

  const handleDelete = async () => {
    const confirmed = await seekConfirmation(
      'Delete post',
      'Are you sure you want to delete this post?',
      'Yes, Go ahead',
    );

    if (confirmed) {
      deletePost({ postId: post.id });
    }
  };

  const renderMenuItem = (Icon, label, clickHandler, disabled = false) => {
    return (
      <Menu.Item>
        {({ active }) => (
          <button
            className={`${
              active && 'bg-primary-light-1'
            } group flex w-full items-center gap-4 p-2`}
            disabled={disabled}
            onClick={clickHandler}>
            <Icon
              className={cx(
                active ? 'text-text-primary' : 'text-text-secondary',
              )}
              aria-hidden="true"
            />
            <Typography
              variant="h5"
              component="span"
              className="w-max"
              color={active ? 'textPrimary' : 'textSecondary'}>
              {label}
            </Typography>
          </button>
        )}
      </Menu.Item>
    );
  };

  return (
    <>
      <Menu as="div" className="relative z-1000 inline-block">
        {hasSession && (
          <div className="flex items-center justify-center">
            <Menu.Button className="rounded-full bg-transparent p-2 hover:bg-body-background disabled:bg-transparent">
              <HiDotsHorizontal aria-hidden="true" />
            </Menu.Button>
          </div>
        )}

        <FadeTransition>
          <Menu.Items className="absolute right-0 mt-1 w-fit min-w-[200px] origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            {canEdit && renderMenuItem(AiOutlineEdit, 'Edit', handleEdit)}
            {canDelete &&
              renderMenuItem(
                RiDeleteBin5Line,
                'Delete this post',
                handleDelete,
              )}
            {canMakePostPublic &&
              renderMenuItem(
                HiOutlineLockOpen,
                isPostPublic
                  ? 'Make this post private'
                  : 'Make this post public',
                handleMakePostPublic,
              )}
            {canMakePostFeatured &&
              // TODO: check
              // (isPostPublic || isNekiAdmin) &&
              renderMenuItem(
                MdOutlineFeaturedVideo,
                isPostFeatured
                  ? 'Remove from featured'
                  : 'Mark this as featured post',
                handleTogglePostAsFeatured,
              )}
            {renderMenuItem(IoFlagSharp, 'Report this post', handleReportPost)}

            {canToggleNewsHomeFeedVisibility &&
              renderMenuItem(
                canMakeNewsVisibleOnHomeFeed ? BiShow : BiHide,
                canMakeNewsVisibleOnHomeFeed
                  ? 'Add to home'
                  : 'Remove from home',
                handleToggleNewsHomeFeedVisibility,
              )}

            {withDonate && (
              <Menu.Item
                as="div"
                className="lg:hidden"
                disabled={!canReceiveDonations}>
                {({ active }) => (
                  <button
                    className={`${
                      active && 'bg-primary-light-1'
                    } group flex w-full items-center gap-4 p-2`}
                    disabled={!canReceiveDonations}
                    onClick={handleDonate}>
                    <HiOutlineHeart
                      className={`${
                        canReceiveDonations
                          ? 'text-text-secondary'
                          : 'text-text-disabled'
                      } ${active && 'hidden'}`}
                      aria-hidden="true"
                    />
                    <HiHeart
                      className={`text-primary ${!active && 'hidden'}`}
                      aria-hidden="true"
                    />
                    <Typography
                      variant="h5"
                      component="span"
                      color={
                        active
                          ? 'textPrimary'
                          : `${
                              canReceiveDonations
                                ? 'textSecondary'
                                : 'textDisabled'
                            }`
                      }>
                      Donate
                    </Typography>
                  </button>
                )}
              </Menu.Item>
            )}
            {isPostPublic && (
              <Menu.Item>
                {({ active }) => (
                  <button
                    className={`${
                      active && 'bg-primary-light-1'
                    } group flex w-full items-center gap-4 p-2`}
                    onClick={handleShare}>
                    <HiOutlineShare
                      className={`${
                        active ? 'text-text-primary' : 'text-text-secondary'
                      }`}
                      aria-hidden="true"
                    />
                    <Typography
                      variant="h5"
                      component="span"
                      color={active ? 'textPrimary' : 'textSecondary'}>
                      Share
                    </Typography>
                  </button>
                )}
              </Menu.Item>
            )}
          </Menu.Items>
        </FadeTransition>
      </Menu>
      {editPostDialogOpen && (
        <CreatePostDialog
          open={editPostDialogOpen}
          post={post}
          onClose={() => {
            setEditPostDialogOpen(false);
          }}
        />
      )}
    </>
  );
}

export default PostMenu;
